import { from, Subscription, tap } from 'rxjs';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DoCheck,
  EventEmitter,
  Input,
  NgZone,
  OnDestroy,
  Output,
  ViewEncapsulation,
} from '@angular/core';

import {
  CardBrand,
  CardMessage,
  ClientSdk,
  ClientSdkInstance,
  CustomStylesAppendedMessage,
  ErrorMessage,
  FailMessage,
  InitConfig,
  InteractionMessage,
  MessageType,
  MountedMessage,
  OrderStatusMessage,
  PaymentDetailsMessage,
  RedirectMessage,
  ResizeMessage,
  SdkLoader,
  SdkMessage,
  SubmitMessage,
  SuccessMessage,
  VerifyMessage,
} from '@solidgate/client-sdk-loader';
import './boot';

type ClientSdkEventsProvider = {
  [key in keyof SdkMessage]: EventEmitter<SdkMessage[key]>;
};

interface PaymentElement {
  merchantData?: InitConfig['merchantData']; // required
  width?: string;
  styles?: InitConfig['styles'];
  formParams?: InitConfig['formParams'];
  googlePayButtonParams?: Omit<InitConfig['googlePayButtonParams'], 'containerId'>;
  applePayButtonParams?: Omit<InitConfig['applePayButtonParams'], 'containerId'>;
  paypalButtonParams?: Omit<InitConfig['paypalButtonParams'], 'containerId'>;
}

@Component({
  standalone: true,
  selector: 'app-solid-payment',
  templateUrl: './solid-payment.component.html',
  styleUrls: ['./solid-payment.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SolidPaymentComponent
  implements DoCheck, AfterViewInit, OnDestroy, ClientSdkEventsProvider, PaymentElement
{
  private static Sdk$ = from(SdkLoader.load()).pipe(
    tap((sdk) => (SolidPaymentComponent.Sdk = sdk)),
  );
  private static Sdk: ClientSdk | null = null;

  public id = `${Math.random()}_${Date.now()}_solid_sdk`;

  @Input() merchantData: PaymentElement['merchantData'];
  @Input() width: PaymentElement['width'];
  @Input() styles: PaymentElement['styles'];
  @Input() formParams: PaymentElement['formParams'];
  @Input() googlePayButtonParams: PaymentElement['googlePayButtonParams'];
  @Input() applePayButtonParams: PaymentElement['applePayButtonParams'];
  @Input() paypalButtonParams: PaymentElement['paypalButtonParams'];
  @Input() applePayContainer: HTMLElement | undefined;
  @Input() googlePayContainer: HTMLElement | undefined;
  @Input() paypalContainer: HTMLElement | undefined;

  @Output() showCardBrandError = new EventEmitter<boolean>();
  @Output() mounted = new EventEmitter<MountedMessage>();
  // eslint-disable-next-line @angular-eslint/no-output-native
  @Output() error = new EventEmitter<ErrorMessage>();
  @Output() fail = new EventEmitter<FailMessage>();
  @Output() orderStatus = new EventEmitter<OrderStatusMessage>();
  // eslint-disable-next-line @angular-eslint/no-output-native
  @Output() resize = new EventEmitter<ResizeMessage>();
  @Output() interaction = new EventEmitter<InteractionMessage>();
  // eslint-disable-next-line @angular-eslint/no-output-native
  @Output() success = new EventEmitter<SuccessMessage>();
  // eslint-disable-next-line @angular-eslint/no-output-native
  @Output() submit = new EventEmitter<SubmitMessage>();
  @Output() formRedirect = new EventEmitter<RedirectMessage>();
  @Output() verify = new EventEmitter<VerifyMessage>();
  @Output() customStylesAppended = new EventEmitter<CustomStylesAppendedMessage>();
  @Output() readyPaymentInstance = new EventEmitter<ClientSdkInstance>();
  @Output() card = new EventEmitter<CardMessage>();
  @Output() paymentDetails = new EventEmitter<PaymentDetailsMessage>();

  private isListenersConnected = false;
  private form: ClientSdkInstance | null = null;
  private subscription = new Subscription();
  private previousInitKey = '';

  constructor(
    private zone: NgZone,
    private cd: ChangeDetectorRef,
  ) {}

  ngDoCheck() {
    const { key } = this.initConfig();

    if (this.previousInitKey && this.previousInitKey !== key) {
      this.subscription.add(SolidPaymentComponent.Sdk$.subscribe((sdk) => this.initForm(sdk)));
    }
  }

  ngAfterViewInit() {
    this.subscription.add(SolidPaymentComponent.Sdk$.subscribe((sdk) => this.initForm(sdk)));

    this.cd.detach();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();

    this.zone.runOutsideAngular(() => {
      SolidPaymentComponent.Sdk?.destroy();
    });
  }

  initForm(sdk: ClientSdk | null) {
    const { config, key } = this.initConfig();

    if (this.previousInitKey === key || !sdk) {
      return;
    }

    this.zone.runOutsideAngular(() => {
      this.previousInitKey = key;
      this.form = sdk.init(config);
    });

    if (!this.form) {
      return;
    }

    if (!this.isListenersConnected) {
      this.connectListeners(this.form);
    }
  }

  private connectListeners(form: ClientSdkInstance): void {
    this.readyPaymentInstance.emit(form);

    this.zone.runOutsideAngular(() => {
      form.on(MessageType.Mounted, (e) => {
        console.log('mounted', e.data);
        return this.mounted.emit(e.data);
      });
      form.on(MessageType.Error, (e) => this.error.emit(e.data));
      form.on(MessageType.Fail, (e) => this.fail.emit(e.data));
      form.on(MessageType.OrderStatus, (e) => this.orderStatus.emit(e.data));
      form.on(MessageType.Resize, (e) => this.resize.emit(e.data));
      form.on(MessageType.Interaction, (e) => this.interaction.emit(e.data));
      form.on(MessageType.Success, (e) => this.success.emit(e.data));
      form.on(MessageType.Submit, (e) => this.submit.emit(e.data));
      form.on(MessageType.Redirect, (e) => this.formRedirect.emit(e.data));
      form.on(MessageType.Verify, (e) => this.verify.emit(e.data));
      form.on(MessageType.CustomStylesAppended, (e) => this.customStylesAppended.emit(e.data));
      form.on(MessageType.Card, (e) => {
        console.log(e);
        if (this.formParams?.cardBrands) {
          if (
            ![...this.formParams.cardBrands, 'UNKNOWN'].includes(
              e.data.card.brand.toLowerCase() as CardBrand,
            )
          ) {
            this.form?.update({
              formParams: { ...this.formParams, allowSubmit: false },
            });
            this.showCardBrandError.emit(true);
          } else {
            this.form?.update({
              formParams: { ...this.formParams, allowSubmit: true },
            });
            this.showCardBrandError.emit(false);
          }
        }

        this.card.emit(e.data);
      });

      this.isListenersConnected = true;
    });
  }

  private initConfig(): {
    config: InitConfig;
    key: string;
  } {
    if (!this.merchantData) {
      throw new Error("Attribute 'merchantData' is required");
    }

    const config: InitConfig = {
      merchantData: this.merchantData,
      formParams: this.formParams,
      styles: this.styles,
      applePayButtonParams: this.applePayButtonParams,
      googlePayButtonParams: this.googlePayButtonParams,
      paypalButtonParams: this.paypalButtonParams,
    };

    this.appendIframeParams(config);
    this.appendPayButtonParams(config, 'googlePayButtonParams', this.googlePayContainer);
    this.appendPayButtonParams(config, 'applePayButtonParams', this.applePayContainer);
    this.appendPayButtonParams(config, 'paypalButtonParams', this.paypalContainer);

    return {
      config,
      key: JSON.stringify(config),
    };
  }

  private appendIframeParams(config: InitConfig): void {
    config.iframeParams = {
      containerId: this.id,
    };

    if (this.width) {
      config.iframeParams.width = this.width;
    }
  }

  private appendPayButtonParams<
    T extends 'googlePayButtonParams' | 'applePayButtonParams' | 'paypalButtonParams',
  >(config: InitConfig, key: T, container: HTMLElement | undefined) {
    const syntheticContainerId = `${this.id}_${key}`;
    const payButtonParams = {
      ...(config[key] || {}),
    } as NonNullable<InitConfig[T]>;

    if (payButtonParams.containerId) {
      delete payButtonParams.containerId;
    }

    if (container) {
      if (container.id && container.id !== syntheticContainerId) {
        console.warn(`Id attribute "${container.id}" of container for ${key} will be overriden`);
      }

      payButtonParams.containerId = syntheticContainerId;
      container.id = payButtonParams.containerId;
    }

    if (Object.keys(payButtonParams).length) {
      config[key] = payButtonParams;
    }
  }
}
